/* One venue as a list row.
 *
 * Shares Index's row vocabulary deliberately (ADR 0003): a kind swatch, a name,
 * a dim sub-line, and icon actions revealed on hover. Map is the third browsing
 * workspace and should cost no new reading pattern; what differs is only that
 * the leading swatch is coloured by kind, because on this screen colour is the
 * link between the row and its pin.
 *
 * A venue with several items does not list them here — Watch hands the whole
 * venue to Detail and the playlist steps through it.
 *
 * Presentational: it emits and the workspace acts.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { iconActions, revealOnHover, shadowReset } from '../../design/base.js';
import type { Venue } from '../../core/types.js';
import { kindMeta, localityOf } from './kinds.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';

@customElement('place-row')
export class PlaceRow extends LitElement {
  static override styles = [
    shadowReset,
    iconActions,
    revealOnHover,
    css`
      :host {
        display: block;
        position: relative;
        border-bottom: 1px solid var(--border);
        /* The sweep's heaviest screen: 506 rows, ~27,000px of scroll content
           (finding 5). Off-screen rows now skip layout and paint entirely, which
           matters most at full height where the list is the whole library rather
           than what is in view (ADR 0012 decision 4). The intrinsic size is the
           measured one-line row height, so the scrollbar does not lurch as rows
           come into range; auto lets a wrapped two-line row correct it once
           rendered and remember the correction. */
        content-visibility: auto;
        contain-intrinsic-size: auto 52px;
        /* The kind icon's box is exactly the venue name's line box, so the two
           align by construction instead of by a hand-tuned margin. Both halves
           of this expression are repeated below on .name — keep them in step, or
           the icon drifts off the name's first line again. */
        --kico-size: calc(0.88rem * 1.35);
      }
      /* The selected row and its pin have to be findable from each other. The
         left bar in the kind's own hue does that without tinting the whole row,
         which at 38 visible rows would read as a second, competing list. */
      :host([data-active]) {
        background: var(--surface);
        box-shadow: inset 3px 0 0 var(--kc);
      }
      .main {
        display: flex;
        align-items: flex-start;
        gap: var(--space-2);
        width: 100%;
        /* Reserve the actions strip so a long name never runs under it and
           revealing the icons on hover cannot shift the text. Three buttons at
           25px each (15px icon + 2×4px pad + 2×1px border) plus two 2px gaps,
           plus the right inset — measured, not guessed, because the naive 3×15
           leaves the name touching the play icon. */
        padding: var(--space-2) 88px var(--space-2) var(--space-3);
        /* The gutter handover (ADR 0009 §3): 0 inside the desktop panel, 16px in
           the mobile sheet where the row is full-bleed and its own background,
           divider and press state have to reach the screen edge. */
        padding-left: var(--row-pad-l);
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        font-family: var(--font-ui);
        text-align: left;
        cursor: pointer;
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips), and
         a row left lit competes with the genuinely selected one above. */
      @media (hover: hover) {
        :host(:hover) .main {
          background: var(--surface-2);
        }
      }
      .main:active {
        background: var(--press);
      }
      .main:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      /* ~19px against a 0.88rem (~14px) name. The kind icon is this row's swatch —
         it ties the row to its pin and to its chip, and at 15px it read as
         punctuation beside the name rather than as the row's marker.

         No margin nudge: sized to the name's line box, its top and the name's
         top are the same edge. The margin it replaces was fighting a strut —
         .name is a BLOCK below for the same reason, because as an inline it was
         laid out in .text's inherited 15px/1.5 line box (22.5px) while the icon
         was 18px, so the two could not agree on where the top was. */
      .kico {
        flex: none;
        color: var(--kc);
        --icon-size: var(--kico-size);
      }
      .text {
        min-width: 0;
        flex: 1;
      }
      /* Block, so the line box comes from this element's own font-size and
         line-height rather than from .text's inherited strut — that is what makes
         --kico-size above a true description of the name's first line. */
      .name {
        display: block;
        font-size: 0.88rem;
        font-weight: 500;
        line-height: 1.35;
        overflow-wrap: anywhere;
      }
      .sub {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
        color: var(--text-dim);
        font-size: 0.76rem;
        line-height: 1.4;
      }
      .locality {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      /* Index's ×N exactly: annotation, dim rather than accent, and shown only
         at n >= 2 so it does not become noise on the rows that carry one item. */
      .count {
        flex: none;
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.72rem;
      }
      .offmap {
        flex: none;
        color: var(--k-off);
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .acts {
        position: absolute;
        top: 6px;
        right: var(--space-2);
      }
      .on {
        color: var(--k-stay);
      }
      /* Visited has to read at rest, but it cannot do so from inside .acts:
         revealOnHover sets opacity on that whole cluster, and opacity creates a
         group — a child cannot opt back in. So the *state* lives in the sub-line
         and the *action* stays in the hover cluster, which is the right split
         anyway. */
      .vis {
        flex: none;
        color: var(--k-stay);
        --icon-size: 12px;
      }
      /* On mobile nothing overlays the row, so the 88px strip reserved for the
         desktop hover cluster is dead space that a long venue name needs back.
         The actions themselves are not hidden here — they are not rendered at
         all (see the mobile property below). */
      @media (max-width: 959px) {
        .main {
          padding-right: var(--row-pad-r);
        }
      }
    `,
  ];

  @property({ attribute: false }) venue!: Venue;
  @property({ type: Boolean, reflect: true, attribute: 'data-active' }) active = false;

  /**
   * Rendering in the mobile list rather than the desktop panel.
   *
   * The row loses its actions entirely: a tap opens `place-detail`, which is
   * where watch, visited and remove live with room to be labelled and spaced
   * (ADR 0012 decisions 7 and 8). Not rendered and then hidden — three
   * g-buttons and three g-icons per row were the bulk of this screen's
   * interactive nodes.
   */
  @property({ type: Boolean }) mobile = false;

  /**
   * Bubbling but NOT composed, deliberately — and this is load-bearing.
   *
   * The event is dispatched on this host, which already sits inside map-panel's
   * shadow tree, so plain bubbling is enough for the panel's template listener
   * to hear it. `composed: true` would also let it escape *past* the panel to
   * the workspace, which listens for the same names on `map-panel` — so the
   * workspace would receive the row's event (no detail) as well as the panel's
   * re-emit (with the venue id), both retargeted to `map-panel` and therefore
   * indistinguishable. The undefined one arrives last and wins: selecting a
   * venue set the selection and then immediately cleared it.
   *
   * Rule of thumb for the rest of v2: compose an event only when it must cross
   * the component that owns the element, and never re-emit under the same name
   * without stopping the original.
   */
  private emit(type: 'select' | 'toggle-visited' | 'remove'): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true }));
  }

  /**
   * Ask for this item, and say which venue asked.
   *
   * The venue is part of the payload rather than something the workspace looks
   * up from the reel id, because that lookup is genuinely ambiguous: an item
   * routinely recommends several places (one in the library names six), so the
   * same reel id belongs to several venues and "find the venue containing it"
   * returns whichever happens to sit earliest in the array. Measured: clicking
   * an item on a three-item venue opened a two-item playlist from a different
   * one. The row is the only thing that knows which venue was clicked.
   */
  private watch(reelId: number): void {
    this.dispatchEvent(
      new CustomEvent('watch', {
        detail: { venueId: this.venue.map_place_id, reelId },
        bubbles: true,
      }),
    );
  }

  /** Watch always hands the whole venue over; the playlist does the rest. */
  private onWatch(): void {
    const first = this.venue.reels[0];
    if (first) this.watch(first.reel_id);
  }

  override render() {
    const v = this.venue;
    if (!v) return nothing;
    const meta = kindMeta(v.kind, v.geocoded);
    const multi = v.reels.length > 1;
    const locality = localityOf(v);
    const only = v.reels[0];
    return html`
      <button class="main" style="--kc: ${meta.color}" @click=${() => this.emit('select')}>
        <g-icon class="kico" name=${meta.icon}></g-icon>
        <span class="text">
          <span class="name">${v.name}</span>
          <span class="sub">
            ${v.visited ? html`<g-icon class="vis" name="check"></g-icon>` : nothing}
            ${locality ? html`<span class="locality">${locality}</span>` : nothing}
            ${v.geocoded ? nothing : html`<span class="offmap">off-map</span>`}
            ${multi ? html`<span class="count">×${v.reels.length}</span>` : nothing}
            ${!multi && only?.title ? html`<span class="locality">${only.title}</span>` : nothing}
          </span>
        </span>
      </button>
      ${this.mobile ? nothing : this.renderActs(multi)}
    `;
  }

  private renderActs(multi: boolean) {
    const v = this.venue;
    return html`
      <span class="acts">
        <g-button icon-only label=${multi ? `Watch ${v.reels.length}` : 'Watch'} @click=${this.onWatch}>
          <g-icon name="play"></g-icon>
        </g-button>
        <g-button
          class=${v.visited ? 'on' : ''}
          icon-only
          label=${v.visited ? 'Visited' : 'Mark visited'}
          @click=${() => this.emit('toggle-visited')}
        >
          <g-icon name="check"></g-icon>
        </g-button>
        <g-button
          variant="danger"
          icon-only
          label="Remove from map"
          @click=${() => this.emit('remove')}
        >
          <g-icon name="trash"></g-icon>
        </g-button>
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'place-row': PlaceRow;
  }
}
